import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  Button,
  CloseButton,
  Col,
  Form,
  Modal,
  Row,
  Spinner
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAddressCard,
  faCheckCircle,
  faClipboardCheck,
  faExclamationTriangle,
  faHandHoldingHeart,
  faInfoCircle,
  faUndoAlt,
  faUserCheck,
  faUserShield,
  faUserTimes,
  faUsersCog
} from '@fortawesome/free-solid-svg-icons';
import IconAlert from 'components/common/IconAlert';
import ThemedSelect from 'components/commonbreadcrumb/ThemedSelect';
import FormatPhoneNumber from 'components/common/FormatPhoneNumber';
import StaffPersonalFields from 'components/admin/facility-staff/add-facility-staff/StaffPersonalFields';
import {
  buildGrantableRoleOptions,
  digitsOnly,
  getCachedGroupFacilities,
  getCachedGroupOptions,
  getGroupFeatureFlags,
  isValidEmail,
  isValidUsername,
  suggestUsernameFromEmail,
  toDayProgramOptions,
  validateStaffPersonalFields,
  validateStaffUsername
} from 'components/admin/facility-staff/add-facility-staff/staffFormRules';
import useRequestState from 'hooks/useRequestState';
import {
  NETWORK_ERROR_MESSAGE,
  approveRegistration,
  checkEmailAvailability,
  checkUsernameAvailability,
  fetchDayPrograms,
  fetchRegistration,
  fetchRegistrationFacilities,
  getApiErrorMessage
} from 'helpers/registrationApi';
import { getLoggedInRoleId } from 'helpers/registrationMeta';
import {
  FacilityChips,
  InitialsAvatar,
  RolePill,
  ToneIcon
} from 'components/registration-shared/RegistrationUI';
import {
  formatRelative,
  getFullName,
  getIpAddress,
  getRequestedFacilities,
  getRequestedRoleId,
  getSubmittedAt,
  getTypedOrganization,
  hasGroup
} from 'components/registration-shared/registrationFormat';

const EMPTY_VALUES = {
  fname: '',
  lname: '',
  mobile: '',
  email: '',
  address: '',
  address2: '',
  city: '',
  state: '',
  zip: ''
};

// Numeric-looking ids as numbers so requested and cached facilities compare.
const toId = value => (/^\d+$/.test(String(value)) ? Number(value) : value);

const sameIds = (a, b) =>
  a.length === b.length &&
  a.every(item => b.some(other => String(other.value) === String(item.value)));

// Server messages that belong next to a specific field.
const mapServerMessageToField = message => {
  const text = String(message || '').toLowerCase();
  if (text.includes('email') && /(exist|registered|taken|in use)/.test(text)) {
    return 'email';
  }
  if (text.includes('username') && /(exist|taken|in use|invalid)/.test(text)) {
    return 'username';
  }
  if (text.includes('group')) {
    return 'group';
  }
  if (/(privilege|not authorized|higher|role)/.test(text)) {
    return 'role';
  }
  if (text.includes('facilit')) {
    return 'facility';
  }
  return null;
};

const Hint = ({ status, messages }) => {
  if (!messages[status]) {
    return null;
  }
  const tone = {
    checking: '',
    available: 'is-success',
    taken: 'is-danger',
    unknown: 'is-warning'
  }[status];
  return (
    <div className={classNames('mym-field-hint', tone)}>
      {status === 'checking' && <Spinner animation="border" size="sm" />}
      {status === 'available' && <FontAwesomeIcon icon={faCheckCircle} />}
      {status === 'taken' && <FontAwesomeIcon icon={faExclamationTriangle} />}
      {messages[status]}
    </div>
  );
};

Hint.propTypes = {
  status: PropTypes.string.isRequired,
  messages: PropTypes.object.isRequired
};

/**
 * The one review screen for a pending request (U4.2 / D8): what was asked
 * for comes first, then what to grant (group first, then role), then the
 * account details — with Approve and Reject side by side. Never a bare
 * confirm: the modal opens pre-filled from the request and every field stays
 * editable, using the same fields and rules as AddFacilityStaff (D9). The
 * applicant's facility list is shown as a REQUEST; what goes in "Facilities
 * to grant" is what the account actually gets (K7).
 */
const ApproveRegistrationModal = ({
  show,
  record,
  onHide,
  onApproved,
  onReject,
  getGroupName,
  lookupFacility
}) => {
  const approverRoleId = getLoggedInRoleId();
  const detailReq = useRequestState();
  const dayProgramsReq = useRequestState({ initialData: [] });
  const submitReq = useRequestState();
  const { run: runDetail } = detailReq;
  const { run: runDayPrograms } = dayProgramsReq;

  const [initialized, setInitialized] = useState(false);
  const [source, setSource] = useState(null);
  const [values, setValues] = useState(EMPTY_VALUES);
  const [username, setUsername] = useState('');
  const [role, setRole] = useState('');
  const [facilities, setFacilities] = useState([]);
  const [dayPrograms, setDayPrograms] = useState([]);
  const [groupFacilityOptions, setGroupFacilityOptions] = useState([]);
  // Group picked by a pharmacy approver for a request that has none.
  const [chosenGroup, setChosenGroup] = useState(null);
  const [touched, setTouched] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [serverError, setServerError] = useState('');
  const [serverFieldErrors, setServerFieldErrors] = useState({});
  const [usernameCheck, setUsernameCheck] = useState('idle');
  const [emailCheck, setEmailCheck] = useState('idle');

  const recordId = record?.id;
  const requestGroupId = source?.group_id ?? record?.group_id;
  const requestHasGroup = hasGroup({ group_id: requestGroupId });
  // The public form doesn't ask for an organization, so some requests arrive
  // without a group. Pharmacy roles (1–3) then pick it here — before the role,
  // because the group decides which roles and facilities are available.
  const needsGroupChoice = approverRoleId <= 3 && !requestHasGroup;
  let groupId;
  if (requestHasGroup) {
    groupId = requestGroupId;
  } else if (needsGroupChoice) {
    groupId = chosenGroup?.value;
  }
  const groupPending = needsGroupChoice && !chosenGroup;
  const submitting = submitReq.isLoading;

  const flags = useMemo(
    () => getGroupFeatureFlags(groupId, approverRoleId),
    [groupId, approverRoleId]
  );
  const roleOptions = useMemo(
    () =>
      buildGrantableRoleOptions({
        approverRoleId,
        isDayProgramActive: flags.isDayProgramActive,
        isSharedLivingActive: flags.isSharedLivingActive
      }),
    [approverRoleId, flags]
  );

  const groupOptions = useMemo(
    () => (needsGroupChoice ? getCachedGroupOptions() : []),
    [needsGroupChoice]
  );

  const requested = useMemo(
    () => (source ? getRequestedFacilities(source, lookupFacility) : []),
    [source, lookupFacility]
  );
  const requestedOptions = useMemo(
    () =>
      requested.map(facility => ({
        value: toId(facility.id),
        label: facility.name
      })),
    [requested]
  );

  /* ---------------------------- open → load + prefill ---------------------------- */

  useEffect(() => {
    if (!show || recordId === undefined || recordId === null) {
      return;
    }
    setInitialized(false);
    setServerError('');
    setServerFieldErrors({});
    setSubmitAttempted(false);
    setTouched({});
    runDetail(() => fetchRegistration(recordId), { keepData: false });
  }, [show, recordId, runDetail]);

  useEffect(() => {
    if (!show || initialized || !record) {
      return;
    }
    if (detailReq.isIdle || detailReq.isLoading) {
      return;
    }
    // Prefer the full record; fall back to the list row if the detail call failed.
    const merged = detailReq.isSuccess
      ? { ...record, ...detailReq.data }
      : record;
    const requestedRoleId = String(getRequestedRoleId(merged) || '');
    const grantable = buildGrantableRoleOptions({
      approverRoleId,
      ...getGroupFeatureFlags(merged.group_id, approverRoleId)
    });

    setSource(merged);
    setValues({
      fname: String(merged.first_name || ''),
      lname: String(merged.last_name || ''),
      mobile: FormatPhoneNumber(digitsOnly(merged.phone)),
      email: String(merged.email || ''),
      address: String(merged.address || ''),
      address2: String(merged.address2 || ''),
      city: String(merged.city || ''),
      state: String(merged.state || '').toUpperCase(),
      zip: digitsOnly(merged.zip).slice(0, 5)
    });
    setUsername(
      String(merged.username || suggestUsernameFromEmail(merged.email))
    );
    setRole(
      grantable.some(
        option => String(option.value) === requestedRoleId && !option.disabled
      )
        ? requestedRoleId
        : ''
    );
    // D8: pre-fill the grant with the request — the admin edits from there.
    setFacilities(
      getRequestedFacilities(merged, lookupFacility).map(facility => ({
        value: toId(facility.id),
        label: facility.name
      }))
    );
    setDayPrograms([]);
    setChosenGroup(null);
    setInitialized(true);
  }, [
    show,
    initialized,
    record,
    detailReq.isIdle,
    detailReq.isLoading,
    detailReq.isSuccess,
    detailReq.data,
    approverRoleId,
    lookupFacility
  ]);

  // Facilities the approver can grant in this group: the login cache, else the
  // group's registration list.
  useEffect(() => {
    if (!show) {
      return undefined;
    }
    if (!groupId) {
      // No organization on the request (the public form doesn't ask for one):
      // never carry over the list from the previously opened request.
      setGroupFacilityOptions([]);
      return undefined;
    }
    const cached = getCachedGroupFacilities(groupId).map(option => ({
      ...option,
      value: toId(option.value)
    }));
    if (cached.length > 0) {
      setGroupFacilityOptions(cached);
      return undefined;
    }
    let cancelled = false;
    fetchRegistrationFacilities(groupId)
      .then(list => {
        if (!cancelled) {
          setGroupFacilityOptions(
            list.map(facility => ({
              value: toId(facility.id),
              label: facility.name
            }))
          );
        }
      })
      .catch(() => {
        if (!cancelled) {
          setGroupFacilityOptions([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [show, groupId]);

  const facilityOptions = useMemo(() => {
    const merged = [...groupFacilityOptions];
    requestedOptions.forEach(option => {
      if (
        !merged.some(
          existing => String(existing.value) === String(option.value)
        )
      ) {
        merged.push(option);
      }
    });
    return merged.sort((a, b) =>
      String(a.label).localeCompare(String(b.label))
    );
  }, [groupFacilityOptions, requestedOptions]);

  // Day programs for roles 5 (optional) and 6 (required), like AddFacilityStaff.
  const needsDayPrograms =
    (role === '6' || role === '5') && flags.isDayProgramActive;

  useEffect(() => {
    if (!show || !needsDayPrograms) {
      return;
    }
    if (dayProgramsReq.isSuccess || dayProgramsReq.isLoading) {
      return;
    }
    runDayPrograms(fetchDayPrograms);
  }, [
    show,
    needsDayPrograms,
    dayProgramsReq.isSuccess,
    dayProgramsReq.isLoading,
    runDayPrograms
  ]);

  const dayProgramOptions = useMemo(
    () => toDayProgramOptions(dayProgramsReq.data, groupId, approverRoleId),
    [dayProgramsReq.data, groupId, approverRoleId]
  );

  /* ------------------------- live availability checks ------------------------ */

  useEffect(() => {
    const value = username.trim();
    if (!show || !initialized || !value || !isValidUsername(value)) {
      setUsernameCheck('idle');
      return undefined;
    }
    let cancelled = false;
    setUsernameCheck('checking');
    const timer = setTimeout(async () => {
      try {
        const available = await checkUsernameAvailability(value);
        if (!cancelled) {
          setUsernameCheck(available ? 'available' : 'taken');
        }
      } catch (error) {
        if (!cancelled) {
          setUsernameCheck('unknown');
        }
      }
    }, 500);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [username, show, initialized]);

  useEffect(() => {
    const value = values.email.trim();
    if (!show || !initialized || !value || !isValidEmail(value)) {
      setEmailCheck('idle');
      return undefined;
    }
    let cancelled = false;
    setEmailCheck('checking');
    const timer = setTimeout(async () => {
      try {
        const available = await checkEmailAvailability(value);
        if (!cancelled) {
          setEmailCheck(available ? 'available' : 'taken');
        }
      } catch (error) {
        if (!cancelled) {
          setEmailCheck('unknown');
        }
      }
    }, 500);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [values.email, show, initialized]);

  /* -------------------------------- validation ------------------------------- */

  const errors = useMemo(() => {
    const found = validateStaffPersonalFields(values);
    const usernameError = validateStaffUsername(username);
    if (usernameError) {
      found.username = usernameError;
    } else if (usernameCheck === 'taken') {
      found.username = 'This username is already taken.';
    }
    if (!found.email && emailCheck === 'taken') {
      found.email = 'This email is already registered.';
    }
    // Group first: until it's chosen, the role and what to assign wait.
    // Messages match AddFacilityStaff's (D9).
    if (groupPending) {
      found.group = 'Group is required.';
    } else {
      if (!role) {
        found.role = 'Role is required.';
      }
      if (role === '5' && facilities.length === 0) {
        found.facility =
          'At least one Facility must be selected for Facility Staff.';
      }
      if (role === '6' && dayPrograms.length === 0) {
        found.day_programs =
          'At least one Day Program must be selected for Day Program User.';
      }
    }
    return found;
  }, [
    values,
    username,
    usernameCheck,
    emailCheck,
    role,
    facilities,
    dayPrograms,
    groupPending
  ]);

  const visibleErrors = useMemo(() => {
    const shown = {};
    Object.keys(errors).forEach(key => {
      if (submitAttempted || touched[key]) {
        shown[key] = errors[key];
      }
    });
    // Duplicates found by the live checks matter even before a field is
    // touched (the values came pre-filled from the request).
    if (usernameCheck === 'taken' && errors.username) {
      shown.username = errors.username;
    }
    if (emailCheck === 'taken' && errors.email) {
      shown.email = errors.email;
    }
    return { ...shown, ...serverFieldErrors };
  }, [
    errors,
    submitAttempted,
    touched,
    serverFieldErrors,
    usernameCheck,
    emailCheck
  ]);

  /* --------------------------------- handlers -------------------------------- */

  const clearServerFeedback = field => {
    setServerError('');
    if (field && serverFieldErrors[field]) {
      setServerFieldErrors(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleFieldChange = e => {
    const { name, value } = e.target;
    setValues(prev => ({ ...prev, [name]: value }));
    clearServerFeedback(name);
  };

  const handleBlur = e => {
    const { name } = e.target;
    if (name) {
      setTouched(prev => ({ ...prev, [name]: true }));
    }
  };

  const handleRoleChange = e => {
    const next = e.target.value;
    setRole(next);
    setTouched(prev => ({ ...prev, role: true }));
    clearServerFeedback('role');
    if (next === '5' && facilities.length === 0) {
      setFacilities(requestedOptions);
    }
    if (next !== '5' && next !== '6') {
      setDayPrograms([]);
    }
  };

  const handleGroupChange = option => {
    setChosenGroup(option || null);
    // Facilities and day programs belong to a group — start again.
    setFacilities([]);
    setDayPrograms([]);
    // Keep the role only if the new group can hold it (Day Programs /
    // SharedLiving are per-group features).
    const nextRoleOptions = buildGrantableRoleOptions({
      approverRoleId,
      ...getGroupFeatureFlags(option?.value, approverRoleId)
    });
    if (
      role &&
      !nextRoleOptions.some(
        roleOption => String(roleOption.value) === role && !roleOption.disabled
      )
    ) {
      setRole('');
    }
    setTouched(prev => ({ ...prev, group: true }));
    clearServerFeedback('group');
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (submitting || !initialized) {
      return;
    }
    setSubmitAttempted(true);
    setServerError('');
    setServerFieldErrors({});

    if (Object.keys(errors).length > 0) {
      // Same order as on screen: access to grant, then account details.
      const firstField = [
        ['group', 'approve-group'],
        ['role', 'approve-role'],
        ['facility', 'approve-facilities'],
        ['day_programs', 'approve-day-programs'],
        ['fname', 'approve-fname'],
        ['lname', 'approve-lname'],
        ['mobile', 'approve-mobile'],
        ['email', 'approve-email'],
        ['city', 'approve-city'],
        ['state', 'approve-state'],
        ['zip', 'approve-zip'],
        ['username', 'approve-username']
      ].find(([name]) => errors[name]);
      if (firstField) {
        document.getElementById(firstField[1])?.focus();
      }
      return;
    }

    const roleId = Number(role);
    const payload = {
      id: record.id,
      first_name: values.fname.trim(),
      last_name: values.lname.trim(),
      email: values.email.trim(),
      username: username.trim(),
      phone: digitsOnly(values.mobile),
      address: values.address.trim(),
      address2: values.address2.trim(),
      city: values.city.trim(),
      state: values.state,
      zip: values.zip.trim(),
      role_id: roleId,
      ...(needsGroupChoice && chosenGroup
        ? { group_id: toId(chosenGroup.value) }
        : {}),
      facilities: roleId === 5 ? facilities.map(option => option.value) : [],
      day_programs:
        roleId === 5 || roleId === 6
          ? dayPrograms.map(option => option.value)
          : []
    };

    const result = await submitReq.run(() => approveRegistration(payload));
    if (result.stale) {
      return;
    }
    if (!result.ok) {
      const message = getApiErrorMessage(result.error, NETWORK_ERROR_MESSAGE);
      setServerError(message);
      toast.error(message);
      return;
    }

    const response = result.data;
    if (response.ok) {
      onApproved({ record, payload, response });
      return;
    }

    // Surface the server's words — e.g. the privilege-escalation error must be
    // shown, never swallowed (U4.3 / K3).
    const message = response.message || 'Unable to approve this request.';
    const field = mapServerMessageToField(message);
    if (field) {
      setServerFieldErrors({ [field]: message });
    }
    setServerError(message);
    toast.error(message);
  };

  /* ---------------------------------- render --------------------------------- */

  const groupName = source ? getGroupName(source) : '';
  const typedOrganization = source ? getTypedOrganization(source) : '';
  const assignedGroupName = needsGroupChoice
    ? chosenGroup?.label || ''
    : groupName;
  const requestedRoleId = source ? getRequestedRoleId(source) : null;
  const ipAddress = source ? getIpAddress(source) : '';

  const renderAssignment = () => {
    if (role === '4') {
      return (
        <div className="mym-inline-note mym-tone-purple">
          <FontAwesomeIcon icon={faUsersCog} className="mt-1" />
          <div>
            {assignedGroupName ? (
              <>
                Group Admins manage all of <strong>{assignedGroupName}</strong>,
                so there are no facilities to assign.
              </>
            ) : (
              'Group Admins manage a whole group, so there are no facilities to assign.'
            )}
          </div>
        </div>
      );
    }

    if (role === '7') {
      return (
        <div className="mym-inline-note mym-tone-pink">
          <FontAwesomeIcon icon={faHandHoldingHeart} className="mt-1" />
          <div>
            SharedLiving caregivers get access through their assigned patients.
            After you approve, you'll get a link to assign them.
          </div>
        </div>
      );
    }

    if (role !== '5' && role !== '6') {
      return (
        <div className="text-600 fs-10">
          {groupPending
            ? 'Choose a group, then a role, to see what to assign.'
            : 'Choose a role to see what to assign.'}
        </div>
      );
    }

    return (
      <>
        {role === '5' && (
          <Form.Group className="mb-3">
            <Form.Label className="required" htmlFor="approve-facilities">
              Facilities to grant
            </Form.Label>
            <div
              className={classNames('mym-select mym-select-sm', {
                'is-invalid': !!visibleErrors.facility
              })}
            >
              <ThemedSelect
                inputId="approve-facilities"
                isMulti
                closeMenuOnSelect={false}
                options={facilityOptions}
                value={facilities}
                isDisabled={groupPending}
                onChange={selected => {
                  setFacilities(selected || []);
                  setTouched(prev => ({ ...prev, facility: true }));
                  clearServerFeedback('facility');
                }}
                placeholder={
                  groupPending
                    ? 'Choose a group first'
                    : 'Choose the facilities to grant'
                }
                noOptionsMessage={() => 'No facilities found for this group'}
              />
            </div>
            {visibleErrors.facility && (
              <div className="invalid-feedback d-block">
                {visibleErrors.facility}
              </div>
            )}
            {requestedOptions.length > 0 &&
              !sameIds(facilities, requestedOptions) && (
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 mt-1 fs-11"
                  onClick={() => setFacilities(requestedOptions)}
                >
                  <FontAwesomeIcon icon={faUndoAlt} className="me-1" />
                  Reset to the requested facilities
                </Button>
              )}
          </Form.Group>
        )}

        {needsDayPrograms && (
          <Form.Group className="mb-2">
            <Form.Label
              className={role === '6' ? 'required' : ''}
              htmlFor="approve-day-programs"
            >
              {role === '6'
                ? 'Day programs to grant'
                : 'Day programs (optional)'}
            </Form.Label>
            <div
              className={classNames('mym-select mym-select-sm', {
                'is-invalid': !!visibleErrors.day_programs
              })}
            >
              <ThemedSelect
                inputId="approve-day-programs"
                isMulti
                closeMenuOnSelect={false}
                options={groupPending ? [] : dayProgramOptions}
                value={dayPrograms}
                isDisabled={groupPending}
                isLoading={dayProgramsReq.isLoading}
                onChange={selected => {
                  setDayPrograms(selected || []);
                  setTouched(prev => ({ ...prev, day_programs: true }));
                }}
                placeholder={
                  groupPending
                    ? 'Choose a group first'
                    : dayProgramsReq.isLoading
                    ? 'Loading day programs…'
                    : 'Choose day programs'
                }
                noOptionsMessage={() => 'No active day programs in this group'}
              />
            </div>
            {visibleErrors.day_programs && (
              <div className="invalid-feedback d-block">
                {visibleErrors.day_programs}
              </div>
            )}
            {dayProgramsReq.isError && (
              <div className="mym-field-hint is-warning">
                <FontAwesomeIcon icon={faExclamationTriangle} />
                Couldn't load day programs.{' '}
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 fs-11 align-baseline"
                  onClick={() => runDayPrograms(fetchDayPrograms)}
                >
                  Try again
                </Button>
              </div>
            )}
          </Form.Group>
        )}
      </>
    );
  };

  const submittedAt = source ? getSubmittedAt(source) : null;

  return (
    <Modal
      show={show}
      onHide={submitting ? undefined : onHide}
      size="lg"
      className="mym-queue-modal"
      backdrop={submitting ? 'static' : true}
      keyboard={!submitting}
      aria-labelledby="approve-registration-title"
    >
      <div className="mym-modal-head mym-tone-primary">
        <ToneIcon icon={faClipboardCheck} tone="primary" size="lg" solid />
        <div className="min-w-0">
          <h5 id="approve-registration-title" className="mym-modal-title">
            Review request
          </h5>
          <p className="mym-modal-sub">
            Check the request, choose what to grant, then approve or reject.
          </p>
        </div>
        <CloseButton
          onClick={onHide}
          disabled={submitting}
          aria-label="Close"
        />
      </div>

      <Form noValidate onSubmit={handleSubmit}>
        <Modal.Body className="p-3 p-md-4">
          {!initialized ? (
            <div aria-busy="true" aria-live="polite">
              <div className="mym-applicant-summary mb-3">
                <span
                  className="mym-skeleton-line flex-shrink-0"
                  style={{
                    width: '2.25rem',
                    height: '2.25rem',
                    borderRadius: '50%'
                  }}
                />
                <div className="flex-1">
                  <div
                    className="mym-skeleton-line mb-2"
                    style={{ width: '40%' }}
                  />
                  <div className="mym-skeleton-line" style={{ width: '60%' }} />
                </div>
              </div>
              {[0, 1, 2, 3].map(row => (
                <Row key={row} className="g-2 mb-3">
                  <Col md={6}>
                    <div
                      className="mym-skeleton-line"
                      style={{ height: '2rem' }}
                    />
                  </Col>
                  <Col md={6}>
                    <div
                      className="mym-skeleton-line"
                      style={{ height: '2rem' }}
                    />
                  </Col>
                </Row>
              ))}
              <span className="visually-hidden">Loading the request…</span>
            </div>
          ) : (
            <>
              {serverError && (
                <IconAlert variant="danger" className="mb-3">
                  <p className="mb-0 fw-semibold">{serverError}</p>
                </IconAlert>
              )}
              {detailReq.isError && (
                <div className="mym-inline-note mym-tone-warning mb-3">
                  <FontAwesomeIcon icon={faInfoCircle} className="mt-1" />
                  <div>
                    Some details didn't load, so a few fields (like the address)
                    may be empty. You can still fill them in.
                  </div>
                </div>
              )}

              {/* Who is asking */}
              <div className="mym-applicant-summary mb-3">
                <InitialsAvatar record={source} />
                <div className="flex-1 min-w-0">
                  <div className="fw-bold text-900 text-truncate">
                    {getFullName(source)}
                  </div>
                  <div className="fs-11 text-600 text-truncate">
                    {[
                      source.email,
                      submittedAt && `Requested ${formatRelative(submittedAt)}`,
                      ipAddress && `IP ${ipAddress}`
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </div>
                </div>
              </div>

              {/* What they asked for — the decision starts here. */}
              <div className="mym-inline-note mym-tone-info mb-3">
                <FontAwesomeIcon icon={faInfoCircle} className="mt-1" />
                <div className="flex-1 min-w-0">
                  <div className="fw-bold mb-2">Access requested</div>
                  <dl className="mym-kv mb-2">
                    <dt>Organization</dt>
                    <dd>
                      {requestHasGroup || source.group_name ? (
                        groupName
                      ) : typedOrganization ? (
                        <>
                          {typedOrganization}
                          <div className="fs-11 fw-normal text-600">
                            Entered by the applicant — not matched to a group
                          </div>
                        </>
                      ) : (
                        <span className="fw-normal text-600">
                          Not specified
                        </span>
                      )}
                    </dd>
                    <dt>Role</dt>
                    <dd>
                      {requestedRoleId ? (
                        <RolePill roleId={requestedRoleId} />
                      ) : (
                        <span className="fw-normal text-600">
                          Not specified
                        </span>
                      )}
                    </dd>
                    <dt>Facilities</dt>
                    <dd>
                      <FacilityChips
                        facilities={requested}
                        max={0}
                        emptyText="Not specified"
                      />
                    </dd>
                  </dl>
                  <div className="fs-11">
                    Nothing is granted until you approve. What you choose below
                    is what this person gets.
                  </div>
                </div>
              </div>

              {/* What to grant: the broad group first, then the role. */}
              <div className="mym-form-block">
                <div className="mym-form-block-title">
                  <ToneIcon icon={faUserShield} tone="purple" size="sm" />
                  Access to grant
                </div>
                <Row className="gx-2 gy-3 mb-3">
                  <Col md={6}>
                    {needsGroupChoice ? (
                      <Form.Group>
                        <Form.Label
                          className="required"
                          htmlFor="approve-group"
                        >
                          Group
                        </Form.Label>
                        <div
                          className={classNames('mym-select mym-select-sm', {
                            'is-invalid': !!visibleErrors.group
                          })}
                        >
                          <ThemedSelect
                            inputId="approve-group"
                            options={groupOptions}
                            value={chosenGroup}
                            onChange={handleGroupChange}
                            placeholder="Choose a group"
                            noOptionsMessage={() => 'No groups found'}
                          />
                        </div>
                        {visibleErrors.group && (
                          <div className="invalid-feedback d-block">
                            {visibleErrors.group}
                          </div>
                        )}
                        {groupOptions.length === 0 ? (
                          <div className="mym-field-hint is-warning">
                            <FontAwesomeIcon icon={faExclamationTriangle} />
                            The group list isn't loaded. Open the Dashboard,
                            then come back to this request.
                          </div>
                        ) : (
                          !visibleErrors.group && (
                            <div className="mym-field-hint">
                              Not matched to a group yet. Choose one first.
                            </div>
                          )
                        )}
                      </Form.Group>
                    ) : (
                      <Form.Group controlId="approve-group">
                        <Form.Label>Group</Form.Label>
                        <Form.Control
                          plaintext
                          readOnly
                          size="sm"
                          className="fw-semibold"
                          value={assignedGroupName || '—'}
                        />
                      </Form.Group>
                    )}
                  </Col>
                  <Col md={6}>
                    <Form.Group controlId="approve-role">
                      <Form.Label className="required">Role</Form.Label>
                      <Form.Select
                        size="sm"
                        name="role"
                        value={role}
                        onChange={handleRoleChange}
                        onBlur={handleBlur}
                        isInvalid={!!visibleErrors.role}
                        disabled={groupPending}
                      >
                        <option value="">
                          {groupPending
                            ? 'Choose a group first'
                            : 'Select a Role'}
                        </option>
                        {roleOptions.map(option => (
                          <option
                            key={option.value}
                            value={String(option.value)}
                            disabled={!!option.disabled}
                          >
                            {option.key}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {visibleErrors.role}
                      </Form.Control.Feedback>
                      {!visibleErrors.role && (
                        <div className="mym-field-hint">
                          Only roles up to your own level are listed.
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                </Row>

                {renderAssignment()}
              </div>

              <div className="mym-form-block mb-0">
                <div className="mym-form-block-title">
                  <ToneIcon icon={faAddressCard} tone="primary" size="sm" />
                  Account details
                </div>
                <StaffPersonalFields
                  values={values}
                  errors={visibleErrors}
                  onChange={handleFieldChange}
                  onBlur={handleBlur}
                  idPrefix="approve"
                  emailFeedback={
                    <Hint
                      status={emailCheck}
                      messages={{
                        checking: 'Checking email…',
                        unknown: "Couldn't check this email right now."
                      }}
                    />
                  }
                />
                <Row className="gx-2 gy-3 mb-2">
                  <Col md={6}>
                    <Form.Group controlId="approve-username">
                      <Form.Label className="required">User Name</Form.Label>
                      <Form.Control
                        size="sm"
                        name="username"
                        value={username}
                        placeholder="User Name"
                        autoComplete="off"
                        onChange={e => {
                          setUsername(e.target.value);
                          clearServerFeedback('username');
                        }}
                        onBlur={handleBlur}
                        isInvalid={!!visibleErrors.username}
                      />
                      <Form.Control.Feedback type="invalid">
                        {visibleErrors.username}
                      </Form.Control.Feedback>
                      {!visibleErrors.username && (
                        <Hint
                          status={usernameCheck}
                          messages={{
                            checking: 'Checking availability…',
                            available: 'Username is available',
                            unknown: "Couldn't check availability right now."
                          }}
                        />
                      )}
                    </Form.Group>
                  </Col>
                </Row>
              </div>
            </>
          )}
        </Modal.Body>

        {/* Approve and reject live on the same screen. */}
        <Modal.Footer className="justify-content-between">
          <Button
            variant="falcon-danger"
            onClick={() => onReject(record)}
            disabled={submitting || !record}
          >
            <FontAwesomeIcon icon={faUserTimes} className="me-2" />
            Reject
          </Button>
          <div className="d-flex gap-2">
            <Button
              variant="falcon-default"
              onClick={onHide}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="success"
              disabled={submitting || !initialized}
            >
              {submitting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Approving…
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faUserCheck} className="me-2" />
                  Approve &amp; create account
                </>
              )}
            </Button>
          </div>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

ApproveRegistrationModal.propTypes = {
  show: PropTypes.bool.isRequired,
  record: PropTypes.object,
  onHide: PropTypes.func.isRequired,
  onApproved: PropTypes.func.isRequired,
  onReject: PropTypes.func.isRequired,
  getGroupName: PropTypes.func.isRequired,
  lookupFacility: PropTypes.func.isRequired
};

export default ApproveRegistrationModal;
